// Which schema a schedule row actually lives in.
//
// The cross-schema fan-outs (search results, the Providers dropdown, patient
// suggestions, the roster) tag every row they merge with `_schema`. That tag is
// load-bearing for writes, not just for the little provider chip in the UI:
// `getSupabase()` hands back a client hard-bound to the VIEWER's schema
// (`db: { schema: supabaseSchema }`), so an unqualified
//
//     supabase.from('notes-c').update(rec).eq('id', <foreign uuid>).select()
//
// runs `UPDATE <viewer-schema>."notes-c" WHERE id = ...`, matches zero rows, and
// PostgREST answers `200 []` — no error. The save silently does nothing, and any
// caller that then reads `data[0].id` dies with "Cannot read properties of
// undefined (reading 'id')" far away from the real cause.
//
// A falsy/home `_schema` returns the client untouched, so single-schema
// behaviour is byte-identical to what it was before the tag existed.

export const PUBLIC_SCHEMA = "public";

const trimmed = (v) => String(v ?? "").trim();

/**
 * The row's schema when it is NOT the viewer's own, else null.
 *
 * @param {object|null} row         A schedule/roster row, possibly `_schema`-tagged.
 * @param {string|null} homeSchema  The viewer's active schema (`supabaseSchema`).
 * @returns {string|null}
 */
export function foreignSchemaOf(row, homeSchema) {
  const rowSchema = trimmed(row?._schema);
  if (!rowSchema) return null;
  return rowSchema === trimmed(homeSchema) ? null : rowSchema;
}

/**
 * The client to run this row's table work through: schema-scoped for a
 * cross-schema row, the client itself for a home row.
 *
 * Note `client.schema()` returns a PostgrestClient — it has `.from()` and
 * `.rpc()` but NO `.storage` and NO `.auth`. Keep using the original client for
 * those (storage is bucket-scoped anyway; the schema lives in the object path).
 */
export function dbForRow(client, row, homeSchema) {
  const foreign = foreignSchemaOf(row, homeSchema);
  if (!foreign || typeof client?.schema !== "function") return client;
  return client.schema(foreign);
}

/**
 * Schema to build this row's STORAGE paths with (`New/MedADocs/{schema}/…`,
 * audio uploads, …). Unlike the table client this is never null — a home row
 * keeps using the viewer's schema, and an unknown schema falls back to public.
 */
export function storageSchemaOf(row, homeSchema) {
  return trimmed(row?._schema) || trimmed(homeSchema) || PUBLIC_SCHEMA;
}
